
import { supabase } from "@/integrations/supabase/client";
import { LeaderboardData } from "./types";

export const loadConversionRate = async (): Promise<string> => {
  try {
    const { data, error } = await supabase
      .from('bridgeplan')
      .select('bridge_value')
      .eq('SN', 0)
      .eq('bridge_component', 'Order to Revenue Conversion Rate')
      .single();
    
    if (error) {
      console.error("Error fetching conversion rate:", error);
      return "";
    }
    
    if (data) {
      console.log("Loaded conversion rate from DB:", data.bridge_value);
      return data.bridge_value.toString();
    } else {
      return "";
    }
  } catch (err) {
    console.error("Exception when loading conversion rate:", err);
    return "";
  }
};

export const saveConversionRateToSupabase = async (value: string): Promise<void> => {
  try {
    const numericValue = value === '' ? 0 : parseFloat(value);
    
    // Get the current user
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      console.error("Error getting current user:", userError);
      return;
    }
    
    const userId = userData.user?.id;
    
    const { data, error: fetchError } = await supabase
      .from('bridgeplan')
      .select('*')
      .eq('SN', 0)
      .eq('bridge_component', 'Order to Revenue Conversion Rate')
      .single();
    
    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error("Error fetching conversion rate record:", fetchError);
      return;
    }
    
    if (data) {
      const { error } = await supabase
        .from('bridgeplan')
        .update({
          bridge_value: numericValue,
          updated_at: new Date().toISOString(),
          user_id: userId
        })
        .eq('SN', 0)
        .eq('bridge_component', 'Order to Revenue Conversion Rate');
      
      if (error) {
        console.error("Error updating conversion rate in bridgeplan:", error);
      } else {
        console.log("Successfully updated conversion rate to:", numericValue);
      }
    } else {
      const { error } = await supabase
        .from('bridgeplan')
        .insert([{
          bridge_component: 'Order to Revenue Conversion Rate',
          bridge_value: numericValue,
          SN: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          user_id: userId
        }]);
      
      if (error) {
        console.error("Error inserting conversion rate in bridgeplan:", error);
      } else {
        console.log("Successfully inserted conversion rate:", numericValue);
      }
    }
  } catch (err) {
    console.error("Exception when updating conversion rate:", err);
  }
};

export const updateLateStageWinsInBridgeplan = async (value: number): Promise<void> => {
  try {
    const roundedValue = Math.round(value);
    
    const { data, error: fetchError } = await supabase
      .from('bridgeplan')
      .select('*')
      .eq('bridge_component', 'Late Stage Wins')
      .single();
    
    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error("Error fetching Late Stage Wins record:", fetchError);
      return;
    }
    
    if (data) {
      const { error } = await supabase
        .from('bridgeplan')
        .update({
          bridge_value: roundedValue,
          updated_at: new Date().toISOString()
        })
        .eq('bridge_component', 'Late Stage Wins');
      
      if (error) {
        console.error("Error updating Late Stage Wins in bridgeplan:", error);
      } else {
        console.log("Successfully updated Late Stage Wins to:", roundedValue);
      }
    } else {
      const { error } = await supabase
        .from('bridgeplan')
        .insert([{
          bridge_component: 'Late Stage Wins',
          bridge_value: roundedValue,
          SN: 1,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }]);
      
      if (error) {
        console.error("Error inserting Late Stage Wins in bridgeplan:", error);
      } else {
        console.log("Successfully inserted Late Stage Wins:", roundedValue);
      }
    }
  } catch (err) {
    console.error("Exception when updating bridgeplan:", err);
  }
};

export const fetchCountries = async (selectedRegion: string): Promise<string[]> => {
  try {
    let query = supabase.from('leaderboard').select('country');
    
    if (selectedRegion !== "All") {
      query = query.eq('region', selectedRegion);
    }
    
    const { data, error } = await query;
    
    if (error) {
      throw new Error(error.message);
    }
    
    const countries = data
      .map(item => item.country)
      .filter(country => country !== null && country !== undefined && country !== "") as string[];
    
    const uniqueCountries = [...new Set(countries)];
    return ["All", ...uniqueCountries.sort()];
  } catch (err) {
    console.error("Error fetching country data:", err);
    return ["All"];
  }
};

export const fetchAccountNames = async (
  selectedRegion: string,
  selectedCountry: string
): Promise<string[]> => {
  try {
    let query = supabase.from('leaderboard').select('account_name');
    
    if (selectedRegion !== "All") {
      query = query.eq('region', selectedRegion);
    }
    
    if (selectedCountry !== "All") {
      query = query.eq('country', selectedCountry);
    }
    
    const { data, error } = await query;
    
    if (error) {
      throw new Error(error.message);
    }
    
    const accountNames = data
      .map(item => item.account_name)
      .filter(name => name !== null && name !== undefined && name !== "") as string[];
    
    const uniqueAccountNames = [...new Set(accountNames)];
    return ["All", ...uniqueAccountNames.sort()];
  } catch (err) {
    console.error("Error fetching account name data:", err);
    return ["All"];
  }
};

export const fetchLeaderboardData = async (
  selectedRegion: string,
  selectedCountry: string,
  selectedAccountName: string,
  filterType: "product" | "industry",
  selectedProductLine: string,
  selectedIndustry: string
): Promise<LeaderboardData[]> => {
  try {
    let query = supabase
      .from('leaderboard')
      .select(
        'id, target, achievement, revenue_gap, region, country, product_line, ' +
        'industry, prospecting_stage_value, qualification_stage_value, ' +
        'proposal_stage_value, negotiation_stage_value, late_stage_total_value, ' +
        'late_stage_to_gap_ratio, historical_win_ratio, account_name, name'
      );
    
    if (selectedRegion !== "All") {
      query = query.eq('region', selectedRegion);
    }
    
    if (selectedCountry !== "All") {
      query = query.eq('country', selectedCountry);
    }
    
    if (selectedAccountName !== "All") {
      query = query.eq('account_name', selectedAccountName);
    }
    
    if (filterType === "product" && selectedProductLine !== "All") {
      query = query.eq('product_line', selectedProductLine);
    } else if (filterType === "industry" && selectedIndustry !== "All") {
      query = query.eq('industry', selectedIndustry);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error("Error fetching leaderboard data:", error);
      // Return an empty array instead of throwing an error
      return [];
    }
    
    // Return the data as LeaderboardData[], or an empty array if data is null
    return (data || []) as unknown as LeaderboardData[];
  } catch (err) {
    console.error("Error fetching leaderboard data:", err);
    // Return an empty array with the correct type
    return [];
  }
};
